/**
 * Permissions guide — controlling tool access with approval modes and policies.
 *
 * Covers the three permission modes (autonomous, approval, plan),
 * the approval loop, pre-approving tools, and managing policies.
 */

export const permissionsGuideContent = `
Control which tools your teammate can use with permission modes and pre-approval policies. This lets you build human-in-the-loop workflows where sensitive actions require explicit approval.

## Permission Modes

Set the mode when creating a run:

| Mode | Behavior |
|------|----------|
| \`autonomous\` | Teammate uses all assigned tools freely (default) |
| \`approval\` | Teammate pauses before each tool use and waits for approval |
| \`plan\` | Teammate creates a plan first, then executes after approval |

## Autonomous Mode

The default — the teammate runs without pausing. All assigned tools are auto-approved.

\`\`\`python
from m8tes import M8tes

client = M8tes()

run = client.runs.create(
    teammate_id=1,
    message="Summarize open tickets",
    permission_mode="autonomous",
    stream=False,
)
run = client.runs.poll(run.id)
print(run.output)
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/runs \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"teammate_id": 1, "message": "Summarize tickets", "stream": false, "permission_mode": "autonomous"}'
\`\`\`

Use this for trusted operations like read-only queries, internal reports, or tasks with no side effects.

## Approval Mode

The teammate pauses whenever it wants to use a tool. You poll for pending requests and approve or deny each one:

\`\`\`python
import time

# 1. Start a run in approval mode
run = client.runs.create(
    teammate_id=1,
    message="Send follow-up emails to customers",
    permission_mode="approval",
    stream=False,
)

# 2. Poll for permission requests
while run.status not in ("completed", "failed", "cancelled"):
    requests = client.runs.permissions(run.id)

    for req in requests:
        if req.status == "pending":
            print(f"Tool: {req.tool_name}")
            print(f"Input: {req.tool_input}")

            # 3. Approve or deny
            client.runs.approve(
                run.id,
                request_id=req.request_id,
                decision="allow",  # or "deny"
            )

    time.sleep(2)
    run = client.runs.get(run.id)

print(run.output)
\`\`\`

\`\`\`bash
# Start run in approval mode
curl -X POST https://m8tes.ai/api/v2/runs \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"teammate_id": 1, "message": "Send follow-up emails", "stream": false, "permission_mode": "approval"}'

# Check for pending permission requests
curl https://m8tes.ai/api/v2/runs/42/permissions \\
  -H "Authorization: Bearer m8_your_key_here"

# Approve a request
curl -X POST https://m8tes.ai/api/v2/runs/42/approve \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"request_id": "req_abc123", "decision": "allow"}'
\`\`\`

### Remember Decisions

Pass \`remember=True\` to auto-approve the same tool in future runs:

\`\`\`python
client.runs.approve(
    run.id,
    request_id=req.request_id,
    decision="allow",
    remember=True,
)
\`\`\`

## Plan Mode

The teammate creates a plan before executing. It pauses for approval of the plan, then proceeds:

\`\`\`python
run = client.runs.create(
    teammate_id=1,
    message="Reorganize the support queue",
    permission_mode="plan",
    stream=False,
)

# The teammate creates a plan and pauses — approve to execute
while run.status not in ("completed", "failed", "cancelled"):
    for req in client.runs.permissions(run.id):
        if req.status == "pending":
            print(f"Plan: {req.tool_input}")
            client.runs.approve(run.id, request_id=req.request_id, decision="allow")
    time.sleep(2)
    run = client.runs.get(run.id)
\`\`\`

Use plan mode for high-stakes operations where you want to review the approach before execution.

## Pre-Approve Tools

Skip the approval step for specific tools by creating permission policies:

\`\`\`python
client.permissions.create(user_id="cust_123", tool="slack")
client.permissions.create(user_id="cust_123", tool="linear")
\`\`\`

\`\`\`bash
curl -X POST https://m8tes.ai/api/v2/permissions \\
  -H "Authorization: Bearer m8_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"user_id": "cust_123", "tool": "slack"}'
\`\`\`

Now when runs for \`cust_123\` use \`approval\` mode, Slack and Linear calls are auto-approved — only other tools will pause for approval.

### Manage Policies

\`\`\`python
# List permission policies for an end-user
page = client.permissions.list(user_id="cust_123")
for policy in page.data:
    print(f"[{policy.id}] {policy.tool_name}")

# Delete a policy (tool will require approval again)
client.permissions.delete(policy.id, user_id="cust_123")
\`\`\`

\`\`\`bash
curl "https://m8tes.ai/api/v2/permissions?user_id=cust_123" \\
  -H "Authorization: Bearer m8_your_key_here"

curl -X DELETE "https://m8tes.ai/api/v2/permissions/1?user_id=cust_123" \\
  -H "Authorization: Bearer m8_your_key_here"
\`\`\`

### Bulk Pre-Approval During Onboarding

\`\`\`python
def onboard_user(user_id: str, tools: list[str]):
    for tool in tools:
        client.permissions.create(user_id=user_id, tool=tool)

onboard_user("cust_123", ["slack", "linear", "google-sheets"])
\`\`\`

## Production Integration

Use [webhook events](/docs/webhooks-guide) to build an approval UI:

1. Register for \`run.awaiting_input\` webhook events
2. When the webhook fires, poll \`client.runs.permissions(run_id)\` for pending requests
3. Show the request details to your user
4. Call \`client.runs.approve()\` with their decision

## What's Next

- [Webhook Events](/docs/webhooks-guide) — get notified when runs need input
- [Tools](/docs/skills) — connect tools for your end-users
`;
